import { FC, ReactNode } from 'react';
import {
  SectionHeader,
  SectionHeaderProps,
} from '@higo/common/src/features/ExaminationPage/sections/ExaminationPageSection/SectionHeader';
import { StyleProps } from '@summer/jst-react';
import styled, { css } from 'styled-components';
import { ExaminationPageSectionId } from '../../models';
import { HorizontalDivider, media, useTocAnchor } from '@higo/ui';

export type ExaminationPageSectionProps = {
  sectionId?: ExaminationPageSectionId;
  headerExtra?: ReactNode;
  divider?: ReactNode;
  audioDownload?: 'single' | 'double';
  filesIds?: (number | undefined)[];
  isStatusCanceledByUser?: boolean;
  isVisitSection?: boolean;
} & SectionHeaderProps &
  StyleProps;

const Container = styled.section`
  position: relative;
  padding-bottom: ${(props) =>
    ['throat', 'skin', 'ears'].includes(props.id as string) ? '140px' : '0'};
  ${media.mobile.max} {
    padding-bottom: ${(props) =>
      ['throat', 'skin', 'ears'].includes(props.id as string) ? '110px' : '0'};
  }
`;

const VisitContainer = styled.section`
  position: relative;
  padding-bottom: ${(props) =>
    ['throat', 'skin', 'ears'].includes(props.id as string) ? '70px' : '0'};
  ${media.mobile.max} {
    padding-bottom: ${(props) =>
      ['throat', 'skin', 'ears'].includes(props.id as string) ? '40px' : '0'};
  }
`;

const Content = styled.div`
  margin-bottom: 2rem;
`;

export const CanceledInfo = styled('p')(
  ({ theme }) => css`
    margin-top: 0.8rem;
    margin-bottom: 2rem;
    font-size: 0.8rem;
    color: ${theme.palette.secondary[500]};
  `,
);

const SectionAnchor = styled.div`
  position: absolute;
  top: -250px;
`;

export const ExaminationPageSection: FC<ExaminationPageSectionProps> = ({
  sectionId,
  className,
  style,
  children,
  title,
  audioDownload,
  audioLinksToDownload,
  filesIds,
  headerExtra,
  divider = <HorizontalDivider />,
  isStatusCanceledByUser = false,
  isVisitSection = false,
}) => {
  const tocAnchorProps = useTocAnchor(sectionId);
  const isImagingSection = ['throat', 'skin', 'ears'].includes(
    (sectionId as string) || '',
  );

  return !isVisitSection ? (
    <Container
      id={sectionId}
      className={className}
      style={style}
      {...tocAnchorProps}
    >
      {sectionId && <SectionAnchor />}
      <SectionHeader
        title={title}
        audioDownload={audioDownload}
        filesIds={filesIds}
        audioLinksToDownload={audioLinksToDownload}
        id={sectionId ? `${sectionId}-header` : 'section-header'}
      >
        {headerExtra}
      </SectionHeader>
      {isStatusCanceledByUser && <CanceledInfo>Canceled</CanceledInfo>}
      {!(
        isStatusCanceledByUser &&
        isImagingSection
      ) && <Content>{children}</Content>}
      {!['throat', 'skin', 'ears'].includes(sectionId as string) && divider}
    </Container>
  ) : (
    <VisitContainer
      id={sectionId}
      className={className}
      style={style}
      {...tocAnchorProps}
    >
      {sectionId && <SectionAnchor />}

      {isStatusCanceledByUser && <CanceledInfo>Canceled</CanceledInfo>}
      {!(
        isStatusCanceledByUser &&
        isImagingSection
      ) && <Content>{children}</Content>}
    </VisitContainer>
  );
};
